<template>
  <div v-if="visible && !loading && !error" class="organization-health-card">
    <div class="organization-health-header">
      <span class="organization-health-title">Organization health</span>
      <span class="organization-health-score">{{ overallLabel }}</span>
    </div>
    <div class="organization-health-metrics">
      <OrganizationHealthRow
        v-for="row in rows"
        :key="row.key"
        :label="row.label"
        :percent="row.percent"
      />
    </div>
  </div>
  <div v-else-if="visible && loading" class="organization-health-card organization-health-loading">
    <ExPrimarySpinner />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import OrganizationHealthRow from "@components/OrganizationHealthRow/OrganizationHealthRow.vue";
import ExPrimarySpinner from "@components/common/ExPrimarySpinner.vue";
import { getOrganizationHealth } from "@/api/organizationHealth";
import { getStoredCurrentAccountId } from "@/utils/accountProductFlags";
import { LD_FLAG_IDEA_MDP_622 } from "@components/constants/launchDarklyFlagKeys";
import launchDarklyService from "@/services/launchdarkly";

// Organization health card (ER-12272): one completeness bar per measure returned
// by the v2 organization-health endpoint, plus the overall percentage. The card
// never recomputes a value — it renders the payload verbatim.

// Rule 15 order. The label map turns the endpoint's machine keys into the human
// labels; the order list keeps the rows stable regardless of response key order.
// Which rows actually render is driven entirely by the payload (Readiness off →
// the endpoint omits the two Role measures, so only the other two show).
const MEASURE_LABELS = {
  employees_with_position: "Employees with a Position",
  positions_with_role: "Positions with a Role",
  roles_with_responsibilities: "Roles with Responsibilities",
  departments_with_positions: "Departments with Positions",
};
const MEASURE_ORDER = Object.keys(MEASURE_LABELS);

const props = defineProps({
  // Optional for tests; defaults to the stored current account id.
  accountId: {
    type: [String, Number],
    default: null,
  },
});

const visible = ref(false);
const loading = ref(false);
const error = ref(false);
const data = ref(null);

onMounted(async () => {
  try {
    // idea-MDP-622 is a rollout gate for the whole MDP-622 dashboard rework: the
    // card stays hidden until the flag resolves true (fail-closed). The backend
    // organization-health route answers 404 while the flag is off, so skipping
    // the fetch when off avoids a guaranteed error round-trip.
    const flagOn = await launchDarklyService.getFlag(LD_FLAG_IDEA_MDP_622, false);
    if (!flagOn) return;

    const accountId = props.accountId ?? getStoredCurrentAccountId();
    if (accountId == null) {
      error.value = true;
      return;
    }

    visible.value = true;
    loading.value = true;
    const response = await getOrganizationHealth(accountId);
    data.value = response.data;
  } catch (err) {
    // A failed fetch must never break the dashboard: hide the card and log.
    console.error("Failed to load organization health:", err);
    error.value = true;
    visible.value = false;
  } finally {
    loading.value = false;
  }
});

const rows = computed(() => {
  const payload = data.value;
  if (!payload || typeof payload !== "object") return [];
  return MEASURE_ORDER.filter(
    (key) => payload[key] && typeof payload[key] === "object"
  ).map((key) => ({
    key,
    label: MEASURE_LABELS[key],
    percent: payload[key].percent,
  }));
});

const overallLabel = computed(() => {
  const overall = data.value?.overall_percent;
  return overall == null ? "No data" : `Overall ${overall}%`;
});
</script>

<style scoped>
.organization-health-card {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid var(--stroke-not-selcted, #eaecf0);
  background: var(--surface, #ffffff);
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  width: 100%;
}

.organization-health-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}

.organization-health-title {
  color: var(--title-heading, #101828);
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
}

.organization-health-score {
  color: var(--success-color, #079455);
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
}

.organization-health-metrics {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.organization-health-loading {
  align-items: center;
  justify-content: center;
  min-height: 120px;
}
</style>
